import React, {FC, ReactNode, useEffect, useState} from "react";
import CustomTopBar from "./CustomTopBar";
import CustomFooter from "./CustomFooter";
import {useDispatch, useSelector} from "react-redux";
import {IState} from "../../../types/state";
import AcceptCookies from "../../AcceptCookies";
import {AddCartToken} from "../../../store/token";
import jwt_decode, {JwtPayload} from "jwt-decode";
import {setAuth, setCustomerGroupID, setToken} from "../../../store/customer";
import {useRouter} from "next/router";
import {ICategory, IMenu} from "../../../types/productPageTypes";
import {TranslationsType} from "../../../types/homePageTypes";
import CustomHeader from "./CustomHeader";
import CustomMobileMenu from "./CustomHeader/CustomMobileMenu";
import CustomBottomNavigation from "./CustomFooter/CustomBottomNavigation";
import dynamic from "next/dynamic";
import {getCartDataMount} from "../../../store/cart";
import {clearOrders} from "../../../store/orders/ordersActions";
import {mobileMenuClose, mobileSearchClose} from "../../../store/mobile-menu";

const PopupModal = dynamic(() => import('../../popupComponents/PopupModal'), {
    ssr: false,
    loading: () => <p>Loading...</p>
});

export interface ILocales {
    code: string;
    created_at: string | null;
    updated_at: string | null;
    direction: string;
    id: number;
    locale_image: string | null;
    selected?: boolean;
    name: string;
    pivot: {
        channel_id: number;
        locale_id: number
    }
}

export interface ICurrencies {
    code: string;
    created_at: string | null;
    updated_at: string | null;
    id: number;
    name: string;
    symbol: string;
    exchange_rate: {
        created_at: string | null;
        updated_at: string | null;
        id: number;
        rate: string;
        target_currency: number
    }
}

export interface ISocials {
    id: number;
    name: string;
    icon: string;
    link: string;
    created_at: string;
    updated_at: string
}

export interface ICustomTopBar {
    telephone: string;
    dbName: string;
    locales: Array<ILocales>;
    currencies: Array<ICurrencies>;
    selectedLocaleImg: string
    translationsData: TranslationsType;
    selectedRate: ICurrencies;
    selectedLocale: string;
    isSocialLinksActive: Array<Record<string, string>>;
    showAccount: boolean;
    showTopBar: boolean;
    topMenus: IMenu[];
    domain: string;
    themeOptions: {
        font_family: string;
    };
}

export interface IFirstHeader {
    dbName: string;
    logoPath: string;
    domain: string;
    menus: Array<IMenu>;
    hasBlog: string,
    selectedLocaleImg: string,
    locales: Array<ILocales>,
    translationsData: TranslationsType;
    cacheControlVersion: number
    themeOptions: {font_family: string};
    categories: Array<ICategory>;
    isTwoKings: boolean;
    selectedRate: ICurrencies;
    selectedLocale: string;
    backOrderValue: string;
    showAccount: boolean,
    showWishlist: boolean,
    showCart: boolean,
    showSearch: boolean;
    defaultLangCode: string
    isMobile: boolean;
    isSocialLinksActive: Array<Record<string, string>>;
    allowCheckoutValue: string,
    searchShowDetails: {
        searchByPages: "1" | "0";
        searchByPosts: "1" | "0";
        searchByProducts: "1" | "0"
    };
    isOhmTheme: boolean;
    showCategories: boolean;
    metas: {
        meta_description: string;
        meta_keywords: string;
        meta_title: string;
        share_pic: string;
    };
}

export interface IFooter {
    logoPath: string
    selectedLocale: string;
    footerCopyRightValue: string;
    footerPoweredValue: string;
    showSubscriptionStyle: "block" | "none";
    arrFooterTrack: Array<{ href: string, id: string }>;
    footerMenus: Array<IMenu>;
    showFooterEmailStyle: "block" | "none";
    showFooterPhoneStyle: "block" | "none";
    footerEmailValue: string;
    telephone: string;
    showFooterAddressStyle: "block" | "none";
    footerAddressValue: string;
    socialsFooter: Array<ISocials>;
    dbName: string;
    translationsData: TranslationsType,
    cacheControlVersion: number
    showFooterMenuCls: "empty_menus" | "";
    showFooterContactCls: "empty_contacts" | "";
    setPadding: boolean;
    isMobile: boolean;
    themeOptions: {
        show_footer?: boolean;
        font_family: string
    };
    getBuilderData: any;
    domain: string;
    currencies: Array<ICurrencies>;
    backOrderValue: string;
    recaptchaActive: string
}

export interface ICustomMobileMenu {
    menus: Array<IMenu>;
    categories: Array<ICategory>;
    blogValue: string;
    salesActiveSettings: { Tracking: string, ContactWithUs: string };
    translationsData: TranslationsType;
    currencies: Array<ICurrencies>;
    phoneValue: string;
    footerAddressValue: string;
    footerEmailValue: string;
    socialsFooter: Array<ISocials>;
    dbName: string;
    showMobileContactsStyle: "empty_contacts_mobile" | "";
    showMobileSocialsStyle: "empty_socials" | "";
    mobileMenuPagesToggle: boolean;
    locales: Array<ILocales>;
    selectedLocaleImg: string;
    domain: string;
}

export interface ILayout extends ICustomTopBar, IFirstHeader, IFooter, ICustomMobileMenu {
    logoPath: string
    children: ReactNode;
    selectedLocale: string;
    gdprValue: number;
    gdprTitle: string;
    gdprContent: string;
    activeMobileNavElement: number;
    isMobile: boolean;
    themeOptions: {
        font_family: string;
    };
    showAccount: boolean;
    showWishlist: boolean;
    showCart: boolean;
    showSearch: boolean;
    mobileMenuMenus: Array<IMenu>;
    selectedLocaleDirection: "rtl" | "ltr" | null;
    uniqueLayoutId: string
}

const MainLayout: FC<ILayout> = (
    {
        children = <></>,
        telephone = "",
        dbName = "",
        selectedLocale = "en",
        domain = "",
        menus = [],
        hasBlog = "",
        blogValue = "0",
        locales = [],
        currencies = [],
        selectedLocaleImg = "",
        gdprValue = 0,
        gdprTitle = "",
        gdprContent = "",
        categories = [],
        isTwoKings = false,
        translationsData = {},
        backOrderValue = '',
        isSocialLinksActive = [],
        allowCheckoutValue = '',
        footerCopyRightValue = "",
        footerPoweredValue = "",
        showSubscriptionStyle = "none",
        arrFooterTrack = [],
        footerMenus = [],
        showFooterEmailStyle = "none",
        showFooterPhoneStyle = "none",
        footerEmailValue = "",
        footerAddressValue = "",
        showFooterAddressStyle = "none",
        socialsFooter = [],
        showMobileContactsStyle = "",
        showMobileSocialsStyle = "",
        salesActiveSettings = {Tracking: "", ContactWithUs: "",},
        activeMobileNavElement = 0,
        logoPath = "",
        cacheControlVersion = 0,
        isMobile= false,
        currencies: [{code: currCode = ""} = {}] = [],
        showAccount = false,
        showWishlist = false,
        showCart = false,
        showSearch = false,
        showFooterMenuCls = "",
        showFooterContactCls = "",
        defaultLangCode,
        themeOptions,
        getBuilderData,
        recaptchaActive,
        mobileMenuPagesToggle,
        showTopBar,
        isOhmTheme,
        searchShowDetails,
        showCategories,
        metas,
        mobileMenuMenus,
        topMenus,
        selectedLocaleDirection,
        uniqueLayoutId
    }
) => {
    const popUp = useSelector((state: IState) => state.general.popUp);
    const customer = useSelector((state: IState) => state.customer);
    const cartToken = useSelector((state: IState) => state.cartToken);
    const searchOpen = useSelector((state: IState) => state.mobileMenu.searchOpen);
    const {token: customerToken = ""} = useSelector((state: IState) => state.customer);
    const mobileMenuOpen = useSelector((state: IState) => state.mobileMenu.open);
    const [hydrated, setHydrated] = useState(false);
    useEffect(() => setHydrated(true), []);
    const dispatch = useDispatch();
    const {query: {room_id: shortRoomId = null} = {}} = useRouter();

    const backgroundClass = {
        true: "background_layer",
        false: ""
    }
    const bottomNavShow = {
        "true": <CustomBottomNavigation
            activeMobileNavElement={activeMobileNavElement}
            translationsData={translationsData}
        />,
        "false": <></>
    }

    useEffect(() => {
        dispatch(mobileMenuClose());
        (async () => {
            try {
                if (!cartToken?.cartToken) {
                    const response = await fetch("/api/checkout/cart/token");
                    const res = await response.json();
                    if (res.api_token) {
                        dispatch(AddCartToken(res.api_token));
                    }
                }
            } catch (error) {
                console.error("Error fetching cart token:", error);
            }
        })();
        (async () => {
            try {
                dispatch(getCartDataMount(cartToken?.cartToken, customer, selectedLocale, currCode))
            } catch (error) {
                console.error("Error fetching cart mount:", error)
            }

        })()
    }, []);

    useEffect(() => {
        const abortController = new AbortController();
        if (customerToken) {
            const decodedToken: JwtPayload = jwt_decode(customerToken);
            if (Date.now() >= (decodedToken as { exp: number }).exp * 1000) {
                dispatch(setAuth(false));
                dispatch(setCustomerGroupID(1));
                dispatch(setToken(""));
                dispatch(clearOrders());
            }
        }
        return () => abortController.abort();
    }, [customerToken]);

    useEffect(() => {
        if (shortRoomId) {
            sessionStorage.setItem("roomID", shortRoomId as string)
        }
        dispatch(mobileSearchClose());
    }, [])

    return (
        <div className={`site ${uniqueLayoutId}`} style={{ direction: selectedLocaleDirection || "ltr" }}>
            {!isMobile ? <CustomTopBar
                telephone={telephone}
                dbName={dbName}
                locales={locales}
                currencies={currencies}
                selectedLocaleImg={selectedLocaleImg}
                translationsData={translationsData}
                selectedRate={currencies?.[0]}
                selectedLocale={selectedLocale}
                isSocialLinksActive={isSocialLinksActive}
                showAccount={showAccount}
                showTopBar={showTopBar}
                topMenus={topMenus}
                domain={domain}
                themeOptions={themeOptions}
            /> : null}
            <CustomHeader
                logoPath={logoPath}
                dbName={dbName}
                domain={domain}
                menus={menus}
                hasBlog={hasBlog}
                selectedRate={currencies?.[0]}
                isTwoKings={isTwoKings}
                categories={categories}
                selectedLocale={selectedLocale}
                translationsData={translationsData}
                backOrderValue={backOrderValue}
                isSocialLinksActive={isSocialLinksActive}
                allowCheckoutValue={allowCheckoutValue}
                selectedLocaleImg={selectedLocaleImg}
                locales={locales}
                cacheControlVersion={cacheControlVersion}
                isMobile={isMobile}
                showAccount={showAccount}
                showWishlist={showWishlist}
                showCart={showCart}
                showSearch={showSearch}
                defaultLangCode={defaultLangCode}
                themeOptions={themeOptions}
                isOhmTheme={isOhmTheme}
                searchShowDetails={searchShowDetails}
                showCategories={showCategories}
                metas={metas}
            />
            {hydrated ? mobileMenuOpen ? <CustomMobileMenu
                menus={mobileMenuMenus}
                categories={categories}
                blogValue={blogValue}
                salesActiveSettings={salesActiveSettings}
                translationsData={translationsData}
                currencies={currencies}
                phoneValue={telephone}
                footerAddressValue={footerAddressValue}
                footerEmailValue={footerEmailValue}
                socialsFooter={socialsFooter}
                dbName={dbName}
                showMobileContactsStyle={showMobileContactsStyle}
                showMobileSocialsStyle={showMobileSocialsStyle}
                mobileMenuPagesToggle={mobileMenuPagesToggle}
                selectedLocaleImg={selectedLocaleImg}
                locales={locales}
                domain={domain}
            /> : null : null}
            <div className={backgroundClass[`${searchOpen}`]}></div>
            <div className={'site_content'}>{children}</div>
            <CustomFooter
                logoPath={logoPath}
                selectedLocale={selectedLocale}
                footerCopyRightValue={footerCopyRightValue}
                footerPoweredValue={footerPoweredValue}
                showSubscriptionStyle={showSubscriptionStyle}
                arrFooterTrack={arrFooterTrack}
                footerMenus={footerMenus}
                showFooterEmailStyle={showFooterEmailStyle}
                showFooterPhoneStyle={showFooterPhoneStyle}
                footerEmailValue={footerEmailValue}
                telephone={telephone}
                footerAddressValue={footerAddressValue}
                showFooterAddressStyle={showFooterAddressStyle}
                socialsFooter={socialsFooter}
                dbName={dbName}
                translationsData={translationsData}
                cacheControlVersion={cacheControlVersion}
                showFooterMenuCls={showFooterMenuCls}
                setPadding={showCart && showAccount && isMobile}
                showFooterContactCls={showFooterContactCls}
                isMobile={isMobile}
                themeOptions={themeOptions}
                getBuilderData={getBuilderData}
                currencies={currencies}
                backOrderValue={backOrderValue}
                domain={domain}
                recaptchaActive={recaptchaActive}
            />
            {bottomNavShow[`${showCart && showAccount}`]}
            {+gdprValue ? <AcceptCookies
                translationsData={translationsData}
                gdprTitle={gdprTitle}
                gdprContent={gdprContent}
            /> : null}
            {popUp ? <PopupModal
                active={popUp}
                locale={selectedLocale}
                dbName={dbName}
                translationsData={translationsData}
                selectedCurrSymbol={currencies?.[0]?.symbol}
                isSocialLinksActive={isSocialLinksActive}
            />: null}
        </div>
    )
};

export default MainLayout